import React from 'react';
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { DailyReportAuditItem } from '../../../../../../models/audit/DailyReportModel';
import { DailyReportModel } from '../../../../../../models/audit/DailyReportModel';
import PdfCommonHeader from '../shared';

interface LottoOnlyPdfDocumentProps {
  storeName: string;
  address: string;
  reportDate: string;
  dailyReportResponse: DailyReportModel;
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 0,
    paddingTop: 0,
    fontFamily: 'Helvetica',
  },
  tilesWrap: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 6,
    paddingBottom: 6,
  },
  tileCell: {
    width: '25%',
    padding: 6,
    alignItems: 'stretch',
  },
  spacerCell: {
    width: '25%',
    padding: 6,
  },
  tile: {
    width: '100%',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#A3AED0',
    backgroundColor: '#FAFAFC',
  },
  tileHeaderCentered: {
    minHeight: 24,
    backgroundColor: '#ECEEF4',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingLeft: 4,
    paddingRight: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#222B45',
    textAlign: 'center',
  },
  tileBody: {
    padding: 4,
    minHeight: 62,
  },
  tileCenter: {
    minHeight: 32,
    marginTop: 4,
    marginBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileMainTextBlue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#0000FF',
  },
  tileSubText: {
    fontSize: 12,
    lineHeight: 1,
    color: '#6b7280',
    fontStyle: 'italic',
    fontWeight: 600,
    textAlign: 'center',
  },
  tileFooter: {
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    fontSize: 12,
    color: '#111827',
    fontWeight: 700,
    textAlign: 'center',
  },
  shiftSection: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 0,
    paddingBottom: 0,
  },
  shiftTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  shiftTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#222B45',
  },
  shiftTable: {
    width: '100%',
  },
  shiftHeadRow: {
    backgroundColor: '#F0F2F7',
    flexDirection: 'row',
  },
  shiftHeadCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  shiftHeadCellSub: {
    color: '#6b7280',
    fontStyle: 'italic',
    fontSize: 11,
    fontWeight: 400,
  },
  colTime: { width: '33.3333%' },
  colUser: { width: '33.3333%' },
  colInventory: { width: '33.3333%' },
  shiftBodyRow: {
    flexDirection: 'row',
  },
  shiftCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
  },
  shiftCellRight: {
    textAlign: 'right',
  },
  shiftMutedInline: {
    color: '#6b7280',
    fontStyle: 'italic',
  },
  shiftFootRow: {
    flexDirection: 'row',
  },
  shiftFootCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
    fontWeight: 700,
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
  },
  gameSection: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 12,
  },
  gameTitle: {
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 700,
    color: '#222B45',
  },
  gameTable: {
    width: '100%',
  },
  gameHeadRow: {
    backgroundColor: '#F0F2F7',
    flexDirection: 'row',
  },
  gameHeadCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  gColName: { width: '36%' },
  gColRate: { width: '12%' },
  gColPack: { width: '14%' },
  gColRange: { width: '14%' },
  gColAuditQty: { width: '10%' },
  gColAuditAmount: { width: '14%' },
  gameRow: {
    flexDirection: 'row',
  },
  gameSharedCell: {
    justifyContent: 'center',
  },
  gameStackCell: {
    justifyContent: 'flex-start',
  },
  gameStackLine: {
    minHeight: 18,
    justifyContent: 'center',
  },
  gameStackLineDivider: {
    borderBottomWidth: 0,
  },
  gameCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
  },
  gameFootRow: {
    flexDirection: 'row',
  },
  gameFootCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  rateCellGap: {
    paddingRight: 10,
  },
  packCellGap: {
    paddingLeft: 10,
  },
  posLotteryRow: {
    paddingTop: 4,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2C6CA0',
  },
  infoValue: {
    fontSize: 12,
    fontWeight: 400,
    color: '#111827',
  },
  infoSpacer: {
    width: 16,
  },
});

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatStateLotteryReceivedAt = (raw?: string) => {
  const ts = moment(raw);
  if (!ts.isValid()) return '-';
  return `Received at ${ts.format('MM/DD/YYYY hh:mm A')}`;
};

const formatThreeDigitRange = (start: number, end: number) => {
  const startText = Number(start || 0).toString().padStart(3, '0');
  const endText = Number(end || 0).toString().padStart(3, '0');
  return `${startText}-${endText}`;
};

const LottoOnlyPdfDocument: React.FC<LottoOnlyPdfDocumentProps> = ({
  storeName,
  address,
  reportDate,
  dailyReportResponse,
}) => {
  const summaryTotals = (dailyReportResponse as any)?.summaryTotals;
  const serverDailyTotals = (dailyReportResponse as any)?._serverData?.daily_totals;
  const stateLotteryReceivedAt = dailyReportResponse.stateLotteryDataReceivedAt || (dailyReportResponse as any)?._serverData?.state_lottery_data_received_at;
  const stateLotteryText = formatStateLotteryReceivedAt(stateLotteryReceivedAt);

  const auditTotals = serverDailyTotals?.audit ?? summaryTotals?.audit ?? {};
  const lottoTotals = serverDailyTotals?.lotto ?? summaryTotals?.lotto ?? {};

  const auditRevenue = Number(auditTotals?.total_revenue ?? 0);
  const auditSold = Number(auditTotals?.total_sold ?? 0);
  const lottoSales = Number(lottoTotals?.sales ?? 0);
  const lottoPayouts = Number(lottoTotals?.payouts_total ?? 0);
  const audits = Array.isArray(dailyReportResponse?.audits) ? dailyReportResponse.audits : [];
  const totalInventoryRevenue = Number((dailyReportResponse as any)?.auditTotalRevenue ?? auditTotals?.total_revenue ?? 0);
  const totalInventorySold = Number((dailyReportResponse as any)?.auditTotalSold ?? auditTotals?.total_sold ?? 0);
  const games = (((dailyReportResponse as any)?._serverData?.games || []) as any[]);

  return (
    <Document title="Daily Reconciliation Summary">
      <Page size={[816, 1056]} style={styles.page}>
        <PdfCommonHeader storeName={storeName} address={address} reportDate={reportDate} />

        <View style={styles.tilesWrap}>
          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeaderCentered}>
                <Text style={styles.tileTitle}>SCRATCHER</Text>
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  <Text style={styles.tileMainTextBlue}>{formatCurrency(auditRevenue)}</Text>
                  <Text style={styles.tileSubText}>
                    {auditSold} {auditSold === 1 ? 'ticket' : 'tickets'}
                  </Text>
                </View>
                <Text style={styles.tileFooter}>Reported by Audit</Text>
              </View>
            </View>
          </View>

          <View style={styles.spacerCell} />

          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeaderCentered}>
                <Text style={styles.tileTitle}>LOTTO</Text>
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  <Text style={styles.tileMainTextBlue}>{formatCurrency(lottoSales)}</Text>
                </View>
                <Text style={styles.tileFooter}>Reported by Lottery</Text>
              </View>
            </View>
          </View>

          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeaderCentered}>
                <Text style={styles.tileTitle}>PAYOUTS</Text>
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  <Text style={styles.tileMainTextBlue}>{formatCurrency(lottoPayouts)}</Text>
                </View>
                <Text style={styles.tileFooter}>Reported by Lottery</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.shiftSection}>
          <View style={styles.shiftTitleRow}>
            <Text style={styles.shiftTitle}>Scratcher Audits</Text>
          </View>

          <View style={styles.shiftTable}>
            <View style={styles.shiftHeadRow}>
              <Text style={[styles.shiftHeadCell, styles.colTime]}>Time of Audit</Text>
              <Text style={[styles.shiftHeadCell, styles.colUser]}>User</Text>
              <Text style={[styles.shiftHeadCell, styles.colInventory, { textAlign: 'right' }]}>
                Inventory Reported{'\n'}
                <Text style={styles.shiftHeadCellSub}>From display case</Text>
              </Text>
            </View>

            {audits.map((audit: DailyReportAuditItem) => (
              <View style={styles.shiftBodyRow} key={String(audit.audit_id)}>
                <Text style={[styles.shiftCell, styles.colTime]}>
                  {moment(audit.audit_date).format('MM/DD/YYYY, hh:mm A')}
                </Text>
                <Text style={[styles.shiftCell, styles.colUser]}>
                  {audit.user_name || '-'}
                </Text>
                <Text style={[styles.shiftCell, styles.shiftCellRight, styles.colInventory]}>
                  {formatCurrency(Number(audit.audit_summary?.total_revenue ?? 0))}{' '}
                  <Text style={styles.shiftMutedInline}>({Number(audit.audit_summary?.total_sold ?? 0)})</Text>
                </Text>
              </View>
            ))}

            <View style={styles.shiftFootRow}>
              <Text style={[styles.shiftFootCell, { width: '66.6666%' }]}>Total</Text>
              <Text style={[styles.shiftFootCell, styles.shiftCellRight, styles.colInventory]}>
                {formatCurrency(totalInventoryRevenue)}{' '}
                <Text style={styles.shiftMutedInline}>({totalInventorySold})</Text>
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.gameSection}>
          <Text style={styles.gameTitle}>All Games</Text>

          <View style={styles.gameTable}>
            <View style={styles.gameHeadRow} wrap={false}>
              <Text style={[styles.gameHeadCell, styles.gColName]}>Game Name</Text>
              <Text style={[styles.gameHeadCell, styles.gColRate, styles.rateCellGap, { textAlign: 'right' }]}>Rate</Text>
              <Text style={[styles.gameHeadCell, styles.gColPack, styles.packCellGap]}>Pack</Text>
              <Text style={[styles.gameHeadCell, styles.gColRange]}>Range</Text>
              <Text style={[styles.gameHeadCell, styles.gColAuditQty, { textAlign: 'right' }]}>Audit Qty</Text>
              <Text style={[styles.gameHeadCell, styles.gColAuditAmount, { textAlign: 'right' }]}>Audit $</Text>
            </View>

            {(() => {
              const PACK_LINE_HEIGHT = 18;
              type GameBlock = {
                key: string;
                gameName: string;
                rate: number;
                packLines: Array<{ packId: string; range: string }>;
                auditQty: number;
                auditAmount: number;
              };

              const gameBlocks: GameBlock[] = games.map((game: any, gameIdx: number) => {
                const packs = Array.isArray(game?.packs) && game.packs.length > 0
                  ? game.packs
                  : [{ pack_id: '', starting_ticket_number: 0, ending_ticket_number: 0 }];

                const packLines = packs.map((pack: any, packIdx: number) => ({
                  packId: String(pack?.pack_id ?? ''),
                  range: formatThreeDigitRange(
                    Number(pack?.starting_ticket_number ?? 0),
                    Number(pack?.ending_ticket_number ?? 0)
                  ),
                  key: `${game?.game_id ?? gameIdx}-${pack?.pack_id ?? packIdx}-${packIdx}`,
                }));

                return {
                  key: `${game?.game_id ?? gameIdx}`,
                  gameName: String(game?.game_name ?? ''),
                  rate: Number(game?.cost_per_ticket ?? 0),
                  packLines,
                  auditQty: Number(game?.audit_totals?.total_sold ?? 0),
                  auditAmount: Number(game?.audit_totals?.total_revenue ?? 0),
                };
              });

              const totals = gameBlocks.reduce((acc, game) => ({
                rate: acc.rate + game.rate,
                packs: acc.packs + game.packLines.length,
                auditQty: acc.auditQty + game.auditQty,
                auditAmount: acc.auditAmount + game.auditAmount,
              }), {
                rate: 0,
                packs: 0,
                auditQty: 0,
                auditAmount: 0,
              });

              return (
                <>
                  {gameBlocks.map((row) => {
                    const blockHeight = Math.max(1, row.packLines.length) * PACK_LINE_HEIGHT;
                    return (
                      <View key={row.key} style={styles.gameRow} wrap={false}>
                        <View style={[styles.gColName, styles.gameSharedCell, { height: blockHeight }]}>
                          <Text style={styles.gameCell}>{row.gameName}</Text>
                        </View>
                        <View style={[styles.gColRate, styles.gameSharedCell, { height: blockHeight }]}>
                          <Text style={[styles.gameCell, styles.rateCellGap, { textAlign: 'right' }]}>
                            {formatCurrency(row.rate)}
                          </Text>
                        </View>

                        <View style={[styles.gColPack, styles.gameStackCell, { height: blockHeight }]}>
                          {row.packLines.map((packLine, idx) => (
                            <View
                              key={`${row.key}-pack-${idx}`}
                              style={[styles.gameStackLine, idx < row.packLines.length - 1 ? styles.gameStackLineDivider : {}]}
                            >
                              <Text style={[styles.gameCell, styles.packCellGap]}>
                                {packLine.packId}
                              </Text>
                            </View>
                          ))}
                        </View>

                        <View style={[styles.gColRange, styles.gameStackCell, { height: blockHeight }]}>
                          {row.packLines.map((packLine, idx) => (
                            <View
                              key={`${row.key}-range-${idx}`}
                              style={[styles.gameStackLine, idx < row.packLines.length - 1 ? styles.gameStackLineDivider : {}]}
                            >
                              <Text style={styles.gameCell}>
                                {packLine.range}
                              </Text>
                            </View>
                          ))}
                        </View>

                        <View style={[styles.gColAuditQty, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, { textAlign: 'right' }]}>{row.auditQty}</Text>
                        </View>
                        <View style={[styles.gColAuditAmount, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, { textAlign: 'right' }]}>
                            {formatCurrency(row.auditAmount)}
                          </Text>
                        </View>
                      </View>
                    );
                  })}

                  <View style={styles.gameFootRow} wrap={false}>
                    <Text style={[styles.gameFootCell, styles.gColName]}>Total</Text>
                    <Text style={[styles.gameFootCell, styles.gColRate, styles.rateCellGap, { textAlign: 'right' }]}></Text>
                    <Text style={[styles.gameFootCell, styles.gColPack, styles.packCellGap]}>{totals.packs} packs</Text>
                    <Text style={[styles.gameFootCell, styles.gColRange]}></Text>
                    <Text style={[styles.gameFootCell, styles.gColAuditQty, { textAlign: 'right' }]}>{totals.auditQty}</Text>
                    <Text style={[styles.gameFootCell, styles.gColAuditAmount, { textAlign: 'right' }]}>{formatCurrency(totals.auditAmount)}</Text>
                  </View>
                </>
              );
            })()}
          </View>
        </View>

        <View style={styles.posLotteryRow}>
          <Text style={styles.infoLabel}>
            State Lottery Data:
            <Text style={styles.infoValue}> {stateLotteryText}</Text>
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default LottoOnlyPdfDocument;
